import { useState, useCallback } from 'react'
import { Deck, CardType } from '../../types'
import { X, Copy, Download, FileText } from 'lucide-react'

interface ExportModalProps {
  deck: Deck
  onClose: () => void
}

export function ExportModal({ deck, onClose }: ExportModalProps) {
  const [format, setFormat] = useState<'text' | 'json'>('text')
  const [copied, setCopied] = useState(false)

  const exportText = useCallback(() => {
    const lines = [`# ${deck.name}`, '', '## Mazo Principal', '']
    
    const units = deck.cards.filter((c) => c.card.type === CardType.UNIT)
    const spells = deck.cards.filter((c) => c.card.type === CardType.SPELL)
    const artifacts = deck.cards.filter((c) => c.card.type === CardType.ARTIFACT)
    const leaders = deck.cards.filter((c) => c.card.type === CardType.LEADER)

    if (leaders.length > 0) {
      lines.push('### Líderes')
      leaders.forEach((c) => lines.push(`${c.quantity}x ${c.card.name}`))
      lines.push('')
    }

    units.sort((a, b) => a.card.cost - b.card.cost)
    spells.sort((a, b) => a.card.cost - b.card.cost)
    artifacts.sort((a, b) => a.card.cost - b.card.cost)

    if (units.length > 0) {
      lines.push('### Unidades')
      units.forEach((c) => lines.push(`${c.quantity}x ${c.card.name} (${c.card.cost})`))
      lines.push('')
    }

    if (spells.length > 0) {
      lines.push('### Hechizos')
      spells.forEach((c) => lines.push(`${c.quantity}x ${c.card.name} (${c.card.cost})`))
      lines.push('')
    }

    if (artifacts.length > 0) {
      lines.push('### Artefactos')
      artifacts.forEach((c) => lines.push(`${c.quantity}x ${c.card.name} (${c.card.cost})`))
      lines.push('')
    }

    lines.push(`Total: ${deck.cards.reduce((s, c) => s + c.quantity, 0)} cartas`)
    lines.push('')
    lines.push(`Generado por TCG-Gen - ${new Date().toLocaleDateString()}`)

    return lines.join('\n')
  }, [deck])

  const exportJson = useCallback(() => {
    return JSON.stringify(
      {
        name: deck.name,
        description: deck.description,
        format: deck.format,
        cards: deck.cards.map((c) => ({
          cardId: c.cardId,
          name: c.card.name,
          quantity: c.quantity,
        })),
        createdAt: deck.createdAt,
      },
      null,
      2
    )
  }, [deck])

  const content = format === 'text' ? exportText() : exportJson()

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [content])

  const handleDownload = useCallback(() => {
    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${deck.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.${format === 'text' ? 'txt' : 'json'}`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [content, deck.name, format])

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 rounded-lg w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">Exportar Mazo</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 border-b border-gray-700">
          <div className="flex gap-2">
            <button
              onClick={() => setFormat('text')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                format === 'text'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              <FileText className="w-4 h-4" />
              Formato TCG
            </button>
            <button
              onClick={() => setFormat('json')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                format === 'json'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              <FileText className="w-4 h-4" />
              JSON
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-4">
          <pre className="bg-gray-800 p-4 rounded-lg text-sm text-gray-300 overflow-auto max-h-96 whitespace-pre-wrap">
            {content}
          </pre>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-700">
          <button
            onClick={handleCopy}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700"
          >
            <Copy className="w-4 h-4" />
            {copied ? '¡Copiado!' : 'Copiar'}
          </button>
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500"
          >
            <Download className="w-4 h-4" />
            Descargar
          </button>
        </div>
      </div>
    </div>
  )
}
